// Feature 009: Deal Operating System
// Document upload component with drag-and-drop support
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useCallback } from 'react';
import { getCSRFToken } from '../../utils/api';

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'image/png',
  'image/jpeg',
  'text/plain',
  'text/markdown'
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'deck', label: 'Deck / Presentation' },
  { value: 'email', label: 'Email Thread' },
  { value: 'transcript', label: 'Call Transcript' },
  { value: 'contract', label: 'Contract' },
  { value: 'style_guide', label: 'Style Guide' },
  { value: 'tech_spec', label: 'Tech Spec' },
  { value: 'nda', label: 'NDA' },
  { value: 'other', label: 'Other' },
];

const DocumentUpload = ({ documentableType, documentableId, onUploadComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('deck');
  const [title, setTitle] = useState('');

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (file) => {
    setError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('File type not supported. Allowed: PDF, Word, Excel, PowerPoint, Images, CSV, or TXT');
      return;
    }

    if (file.size > 250 * 1024 * 1024) {
      setError('File size exceeds 250MB limit.');
      return;
    }

    setSelectedFile(file);
    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title) {
      setError('Please select a file and provide a title.');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('document[file]', selectedFile);
    formData.append('document[title]', title);
    formData.append('document[document_type]', documentType);
    formData.append('document[documentable_type]', documentableType || 'Opportunity');
    formData.append('document[documentable_id]', documentableId);

    try {
      const response = await fetch('/api/v1/documents', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCSRFToken(),
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        // Use 'details' if available, fall back to 'errors' for compatibility
        const messages = data.details || data.errors || ['Upload failed'];
        throw new Error(Array.isArray(messages) ? messages.join('. ') : messages);
      }

      // Reset form
      setSelectedFile(null);
      setTitle('');
      setDocumentType('deck');

      if (onUploadComplete) {
        onUploadComplete(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h3
        className="text-sm text-[#fffbf7] tracking-[0.15em] uppercase mb-4"
        style={{ fontFamily: "'GT Pressura Mono', monospace" }}
      >
        Upload Document
      </h3>

      {/* Drag and Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
          isDragOver
            ? 'border-[#a1c6a2] bg-[rgba(161,198,162,0.1)]'
            : 'border-[rgba(255,251,247,0.2)] hover:border-[rgba(255,251,247,0.4)]'
        }`}
      >
        <input
          type="file"
          id="file-upload"
          className="hidden"
          accept=".pdf,.pptx,.ppt,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.txt,.md"
          onChange={handleInputChange}
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <svg
            className="mx-auto h-10 w-10 text-[rgba(255,251,247,0.4)]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="mt-3 text-sm text-[rgba(255,251,247,0.6)]">
            <span className="font-medium text-[#a1c6a2] hover:text-[#8ab08b]">
              Click to upload
            </span>{' '}
            or drag and drop
          </p>
          <p className="mt-1 text-xs text-[rgba(255,251,247,0.4)]">
            PDF, Word, Excel, PowerPoint, Images, CSV, or TXT up to 250MB
          </p>
        </label>
      </div>

      {/* Selected File Info */}
      {selectedFile && (
        <div className="mt-4 p-3 bg-black border border-[rgba(255,251,247,0.1)] flex items-center justify-between">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-[rgba(255,251,247,0.4)] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm text-[#fffbf7]">{selectedFile.name}</span>
            <span className="text-xs text-[rgba(255,251,247,0.4)] ml-2">
              ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
          <button
            type="button"
            onClick={() => setSelectedFile(null)}
            className="text-[rgba(255,251,247,0.4)] hover:text-[#c47474] transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Form Fields */}
      {selectedFile && (
        <div className="mt-4 space-y-4">
          <div>
            <label
              htmlFor="title"
              className="block text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase mb-2"
            >
              Document Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-[#0a0a0a] border border-[rgba(255,251,247,0.2)] text-[#fffbf7] placeholder:text-[rgba(255,251,247,0.4)] text-sm focus:outline-none focus:border-[#a1c6a2]"
              placeholder="Enter document title"
            />
          </div>

          <div>
            <label
              htmlFor="documentType"
              className="block text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase mb-2"
            >
              Document Type
            </label>
            <select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-[rgba(255,251,247,0.2)] text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2]"
            >
              {DOCUMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-[rgba(196,116,116,0.1)] border border-[#c47474]">
          <p className="text-sm text-[#c47474]">{error}</p>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && (
        <div className="mt-4">
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || !title}
            className="w-full px-4 py-2 bg-[#a1c6a2] text-black text-[10px] tracking-[0.15em] uppercase font-semibold hover:bg-[#8ab08b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            {uploading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </span>
            ) : (
              'Upload Document'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
